/**
 * Entity Creation Form Module
 * Handles all logic for party/team creation modal forms
 * Uses form type strategies (form-types/) for what differs between party and team forms
 */

import { t } from '../i18n';
import { initTomSelect } from './tom-select-utils';
import { PartyFormStrategy, TeamFormStrategy } from './form-types';
import { request } from '../utils/http';
import { on } from '../utils/events';
import { byId, all, setText } from '../utils/dom';

const EntityForm = {
    // State
    currentStep: 0,
    selectedServices: {
        premium: false,
        pin: false,
        bump: false,
        style: false
    },
    selectedStyle: 'gold',
    activeSocialType: null,
    socials: {
        telegram: null,
        discord: null,
        instagram: null
    },

    // Summoner data for preview
    summonerData: null,

    // Rank slug for role icons (default: gold)
    rankSlug: 'gold',

    // Services the server rendered disabled: pin slots are full, or (promote mode) the party already has them.
    // Set once from the DOM, never change during the session
    locked: {},

    // Promote mode: the modal is step 3 alone, for a party that is already published
    isPromote: false,

    // Tom Select instances
    tomSelects: [],

    // Prices per currency: USD in cents, XTR in whole Telegram Stars (set from data attributes)
    prices: {
        USD: {},
        XTR: {}
    },

    // Currency of the selected payment method: 'XTR' (Telegram Stars) or 'USD' (crypto)
    currency: 'USD',

    // What a Star costs the buyer in-app, for the "≈ $" hint (config services.telegram.star_usd)
    starUsd: 0,

    // DOM elements cache
    form: null,
    progress: null,
    summonerCard: null,

    // Form type: 'party' or 'team'
    formType: 'party',

    // Form type strategy (set based on formType); its initState adds the role state to this object:
    // party: primaryRoleId, secondaryRoleId; team: captainRoleId, lookingForRoles
    typeStrategy: null,

    /**
     * Initialize the form
     */
    init(cta, href) {
        this.form = document.querySelector('.party-form-new');
        if (!this.form) return;

        // Reset state first
        this.reset();

        // Detect form type and set role strategy
        this.formType = this.form.classList.contains('team-form') ? 'team' : 'party';
        // Entry point the modal was opened from (data-cta of the link or the hash source), for the GA funnel.
        // The Riot login link carries it and the form's own path along, so this form comes back with the same
        // entry point after the login
        this.location = cta || 'unknown';
        const formPath = href ? new URL(href, window.location.href).pathname : '';
        all('.pf-btn-riot', this.form).forEach((link) => {
            const url = new URL(link.href);
            url.searchParams.set('from', this.location);
            if (formPath) {
                url.searchParams.set('form', formPath);
            }
            link.href = url;
        });
        this.typeStrategy = this.formType === 'team' ? TeamFormStrategy : PartyFormStrategy;

        // Initialize role state
        this.typeStrategy.initState(this);

        this.progress = byId('pf-progress');
        this.summonerCard = byId('pf-summoner-card');

        // Save the server's disabled services (before any JS manipulation)
        const hasClass = (id, cls) => byId(id)?.classList.contains(cls) || false;
        this.locked = {
            premium: hasClass('pf-premium', 'pf-premium--disabled'),
            pin: hasClass('pf-svc-pin', 'pf-service--disabled'),
            bump: hasClass('pf-svc-bump', 'pf-service--disabled'),
            style: hasClass('pf-svc-style', 'pf-service--disabled'),
        };
        this.isPromote = this.form.classList.contains('promote-form');

        // Initialize prices from DOM
        this.initPricesFromDOM();

        // Bind form events (need rebinding on each modal open)
        this.bindFormEvents();

        this.initSocialsFromHidden();
        this.initTomSelects();

        // Initialize looking-for for teams
        this.typeStrategy.initLookingFor?.(this);

        // Check for pre-loaded summoner data (authenticated users skip step 0)
        const summonerData = this.form.dataset.summoner ? JSON.parse(this.form.dataset.summoner) : null;
        if (this.isPromote) {
            this.summonerData = summonerData;
            this.rankSlug = summonerData.rank?.slug || 'gold';
            this.initPreview();
            this.currentStep = 3;
            byId('pf-step-3').classList.remove('hidden');
            gtag('event', 'promote_open', {'form_type': this.formType, 'location': this.location});
        } else if (summonerData) {
            // Pre-loaded summoner: skip step 0, go directly to step 1
            this.showSummonerCard(summonerData);
            this.setInput('summoner_id', summonerData.summoner_id);
            this.goToStep(1);
        } else {
            // No pre-loaded data: start at step 0
            this.goToStep(0);

            // Focus on riot_id input if exists
            const riotInput = this.form.querySelector('input[name="riot_id"]');
            if (riotInput) {
                setTimeout(() => riotInput.focus(), 100);
            }
        }
    },

    /**
     * Reset state to defaults
     */
    reset() {
        this.currentStep = 0;
        this.selectedServices = {
            premium: false,
            pin: false,
            bump: false,
            style: false
        };
        this.selectedStyle = 'gold';
        this.activeSocialType = null;
        this.socials = {
            telegram: null,
            discord: null,
            instagram: null
        };
        this.summonerData = null;
        this.rankSlug = 'gold';
        this.locked = {};
        this.isPromote = false;
        this.formType = 'party';
        this.typeStrategy = null;
    },

    /**
     * Destroy and cleanup
     */
    destroy() {
        // Destroy TomSelect instances
        this.tomSelects.forEach(ts => {
            try { ts.destroy(); } catch(e) {}
        });
        this.tomSelects = [];

        // Reset state
        this.reset();

        // Clear cached elements
        this.form = null;
        this.progress = null;
        this.summonerCard = null;
    },

    /**
     * Set a form input by name (hidden inputs keep the state the server gets)
     */
    setInput(name, value) {
        const input = this.form.querySelector(`input[name="${name}"]`);
        if (input) input.value = value;
    },

    /**
     * Initialize prices from DOM data attributes
     */
    initPricesFromDOM() {
        const elements = { premium: '#pf-premium', pin: '#pf-svc-pin', bump: '#pf-svc-bump', style: '#pf-svc-style' };

        this.prices = { USD: {}, XTR: {} };
        Object.entries(elements).forEach(([service, selector]) => {
            const el = document.querySelector(selector);
            this.prices.USD[service] = parseInt(el?.dataset.amountUsd, 10) || 0;
            this.prices.XTR[service] = parseInt(el?.dataset.amountXtr, 10) || 0;
        });

        this.currency = document.querySelector('input[name="payment_provider"]:checked')?.dataset.currency || 'USD';
        this.starUsd = parseFloat(byId('pf-pay-hint')?.dataset.starUsd) || 0;
    },

    /**
     * Switch the payment method: labels, total and the publish button follow its currency
     */
    setPaymentMethod(input) {
        this.currency = input.dataset.currency;

        all('.pf-pay-method').forEach((method) => method.classList.remove('pf-pay-method--active'));
        input.closest('.pf-pay-method').classList.add('pf-pay-method--active');

        const attr = this.currency === 'XTR' ? 'data-price-xtr' : 'data-price-usd';
        all('#pf-step-3 [data-price-usd]').forEach((el) => {
            el.innerHTML = this.priceHtml(el.getAttribute(attr));
        });

        this.updateTotal();
        this.updatePublishButton();
    },

    /**
     * Format an amount of the current currency: "2.50 USDT" or "125 ⭐"
     */
    formatAmount(amount) {
        return this.currency === 'XTR' ? amount + ' \u2B50' : (amount / 100).toFixed(2) + ' USDT';
    },

    /**
     * Escape a label and draw ⭐ as the site's SVG star (same as PromotionPrices::html)
     */
    priceHtml(label) {
        const star = byId('pf-star-icon')?.innerHTML || '\u2B50';
        return this.escapeHtml(label || '').replace(/\u2B50\uFE0F?/g, star.trim());
    },

    /**
     * Initialize Tom Select for param selects
     */
    initTomSelects() {
        // Destroy previous instances if re-initializing
        this.tomSelects.forEach(ts => ts.destroy());
        this.tomSelects = [];

        // Single selects without search: region, voice, age, play time, and the team's ranks with their icons
        const single = {create: false, controlInput: null, allowEmptyOption: false};
        all('.pf-region-select, .pf-param-select:not([multiple])', this.form).forEach((el) => {
            this.tomSelects.push(initTomSelect(el, single));
        });

        const withIcon = (data, escape) => {
            const img = data.$option?.dataset.image;
            return (img ? `<img src="${escape(img)}" width="20" style="margin-right:8px;vertical-align:middle"/>` : '') + escape(data.text);
        };
        all('.pf-rank-select', this.form).forEach((el) => {
            this.tomSelects.push(initTomSelect(el, {
                ...single,
                render: {
                    option: (data, escape) => `<div class="option">${withIcon(data, escape)}</div>`,
                    item: (data, escape) => `<div class="item" data-value="${escape(data.value)}">${withIcon(data, escape)}</div>`,
                },
            }));
        });

        // Languages multi-select with custom rendering
        const langSelect = this.form.querySelector('#pf-languages');
        if (langSelect) {
            const ts = initTomSelect(langSelect, {
                create: false,
                maxItems: null,
                render: {
                    // Dropdown option: "Русский" left, "RU" right
                    option: function(data, escape) {
                        const code = data.$option?.dataset?.code || '';
                        return `<div class="option" style="display:flex;justify-content:space-between;align-items:center">${escape(data.text)}<span style="opacity:0.5;font-size:0.8em">${escape(code)}</span></div>`;
                    },
                    // Selected item: just "RU"
                    item: function(data, escape) {
                        const code = data.$option?.dataset?.code || data.text;
                        return `<div class="item" data-value="${escape(data.value)}">${escape(code)}</div>`;
                    }
                }
            });
            this.tomSelects.push(ts);
        }
    },

    /**
     * Bind form-specific events (need to rebind on each modal open)
     */
    bindFormEvents() {
        // Prevent form submit on Enter
        this.form.addEventListener('submit', (e) => e.preventDefault());

        // Enter key on riot_id input and on the region select (TomSelect)
        this.form.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && e.target.closest('input[name="riot_id"], .pf-region-select + .ts-wrapper')) {
                e.preventDefault();
                document.querySelector('.pf-btn-check')?.click();
            }
        });
    },

    /**
     * Bind global delegated events (bind once, live forever)
     */
    bindGlobalEvents() {
        // Checker button (find summoner)
        on('click', '.pf-btn-check', (e, btn) => {
            e.preventDefault();
            this.checkSummoner(btn);
        });

        // Roles (delegated to strategy)
        on('click', '.pf-roles:not(#pf-looking-for) .pf-role', (e, btn) => this.typeStrategy.handleRoleClick(this, btn));

        // Looking-for roles (teams only)
        on('click', '#pf-looking-for .pf-role', (e, item) => this.typeStrategy.toggleLookingFor?.(this, item));

        // Queues
        on('change', '.pf-queue input[type="checkbox"]', (e, input) => {
            input.closest('.pf-queue').classList.toggle('pf-queue--active', input.checked);
        });

        // Comment counter
        on('input', '.pf-textarea', (e, textarea) => this.updateCharCounter(textarea));

        // Team name - update preview
        on('input', '#pf-team-name', () => this.typeStrategy.renderPreview(this, this.summonerData));

        // Socials
        on('click', '.pf-socials-add .soc-icon', (e, btn) => this.activateSocialInput(btn));
        on('click', '.pf-soc-add-btn', () => this.addSocial());
        on('click', '.pf-soc-cancel-btn', () => this.cancelSocialInput());
        on('click', '.soc-entry__remove', (e, btn) => this.removeSocial(btn.closest('.soc-entry')));

        // Accordion
        on('click', '#pf-promo-header', () => this.toggleAccordion());

        // Payment method
        on('change', 'input[name="payment_provider"]', (e, input) => this.setPaymentMethod(input));

        // Services; a click on a style option inside the card picks the style, anywhere else it toggles the card
        const onStyleOption = (e) => e.target.closest('.pf-style-opt') !== null;
        on('click', '#pf-premium', (e) => {
            if (!onStyleOption(e)) this.togglePremium();
        });

        on('click', '#pf-svc-pin', () => this.toggleService('pin'));
        on('click', '#pf-svc-bump', () => this.toggleService('bump'));

        on('click', '#pf-svc-style', (e) => {
            if (!onStyleOption(e)) this.toggleService('style');
        });

        // Style picker
        on('click', '.pf-style-opt', (e, opt) => this.selectStyle(opt.dataset.style));

        // Stepper navigation
        on('click', '#pf-btn-details', () => this.goToStepWithValidation(2));
        on('click', '#pf-btn-promo', () => this.goToStep(3));
        on('click', '#pf-promo-teaser', () => {
            gtag('event', 'promo_teaser_click', {'form_type': this.formType});
            this.goToStepWithValidation(3);
        });
        on('click', '#pf-btn-back-2', () => this.goToStep(1));
        on('click', '#pf-btn-back-3', () => this.goToStep(2));

        // Form submit (all publish buttons)
        on('click', '.pf-btn-publish', (e) => {
            e.preventDefault();
            this.handlePublish();
        });
    },

    /**
     * Check summoner via AJAX
     */
    async checkSummoner(btn) {
        const regionId = this.form.querySelector('select[name="region_id"]').value;
        const riotId = this.form.querySelector('input[name="riot_id"]')?.value;
        const error = this.form.querySelector('.pf-error');
        const loader = this.form.querySelector('.pf-loader');
        const orBlock = this.form.querySelector('.pf-or');
        const riotBtns = all('.pf-btn-riot', this.form);
        const checkBtns = all('.pf-btn-check');

        // Hide previous errors
        error.classList.add('hidden');
        error.textContent = '';

        // Validate
        if (!regionId) {
            error.textContent = t('Укажите регион');
            error.classList.remove('hidden');
            return;
        }

        // Show loader, hide "or" block and Riot button (choice already made)
        loader.classList.remove('hidden');
        orBlock?.classList.add('hidden');
        riotBtns.forEach((link) => link.classList.add('hidden'));
        checkBtns.forEach((b) => b.disabled = true);

        const params = new URLSearchParams({region: regionId});
        if (riotId) params.set('riot_id', riotId);

        try {
            const response = await (await request(btn.dataset.url, {method: 'POST', body: params})).json();
            loader.classList.add('hidden');
            this.showSummonerCard(response);

            // Set summoner_id
            this.setInput('summoner_id', response.summoner_id);

            // Go to step 1
            this.goToStep(1);
        } catch (err) {
            loader.classList.add('hidden');
            // Restore "or" block and Riot button on error
            orBlock?.classList.remove('hidden');
            riotBtns.forEach((link) => link.classList.remove('hidden'));
            checkBtns.forEach((b) => b.disabled = false);

            error.textContent = err.data?.message || t('Призыватель не найден');
            error.classList.remove('hidden');
        }
    },

    /**
     * Show summoner card with data
     */
    showSummonerCard(data) {
        // Save summoner data for preview
        this.summonerData = data;

        // Save rank slug for role icons
        this.rankSlug = data.rank?.slug || 'gold';

        // Update role icons with actual rank
        this.updateRoleIcons();

        // Show the summoner card
        const card = this.summonerCard;
        card.classList.remove('hidden');

        // Avatar, name and level
        card.querySelector('.pf-sum-avatar-img').src = data.icon_url;
        card.querySelector('.pf-sum-name').textContent = data.name;
        card.querySelector('.pf-sum-level').textContent = data.level + ' ' + t('уровень');

        // The rest of the card is the form type's (the party shows the rank)
        this.typeStrategy.onSummonerFound(this, data);

        // Initialize preview with summoner data
        this.initPreview();
    },

    /**
     * Update role button icons with actual rank
     */
    updateRoleIcons() {
        all('.pf-role').forEach((role) => {
            const img = role.querySelector('.pf-role-icon img');
            const roleSlug = role.dataset.roleSlug;
            if (img && roleSlug) {
                img.src = this.roleIconUrl(roleSlug);
            }
        });
    },

    /**
     * Role icon in the colour of the summoner's rank
     */
    roleIconUrl(roleSlug) {
        return `/images/league/positions/${this.rankSlug || 'gold'}/${roleSlug.toLowerCase()}.webp`;
    },

    /**
     * Update character counter
     */
    updateCharCounter(textarea) {
        const len = textarea.value.length;
        const max = parseInt(textarea.getAttribute('maxlength')) || 500;
        const counter = textarea.closest('.pf-comment-wrap').querySelector('.pf-char-counter');

        counter.textContent = `${len} / ${max}`;
        counter.classList.toggle('pf-char-counter--max', len >= max);
        counter.classList.toggle('pf-char-counter--warn', len < max && len > max * 0.85);
    },

    /**
     * Init socials from hidden inputs
     */
    initSocialsFromHidden() {
        // Read existing values from hidden inputs (already rendered in blade)
        const value = (name) => this.form.querySelector(`input[name="${name}"]`)?.value || null;
        this.socials.telegram = value('telegram');
        this.socials.discord = value('discord_tag');
        this.socials.instagram = value('instagram');
    },

    /**
     * Activate social input
     */
    activateSocialInput(btn) {
        const social = btn.dataset.social;

        // Toggle: if clicking the same button, close input
        if (this.activeSocialType === social) {
            this.cancelSocialInput();
            return;
        }

        // Deactivate others
        all('.pf-socials-add .soc-icon').forEach((icon) => icon.classList.remove('active'));
        btn.classList.add('active');

        this.activeSocialType = social;

        // Show input
        const inputWrap = document.querySelector('.pf-soc-input-wrap');
        const input = inputWrap.querySelector('.pf-soc-input');

        const placeholders = {
            telegram: '@username',
            discord: 'Username#1234',
            instagram: 'username'
        };

        input.placeholder = placeholders[social] || '';
        // Load existing value if editing
        input.value = this.socials[social] || '';
        inputWrap.classList.remove('hidden');
        input.focus();
    },

    /**
     * Clean social value (remove URLs, @ symbols, etc.)
     */
    cleanSocialValue(type, value) {
        switch (type) {
            case 'telegram':
                return value
                    .replace(/^(https?:\/\/)?(t\.me|telegram\.me)\//, '')
                    .replace(/^@/, '')
                    .replace(/\/$/, '');
            case 'instagram':
                return value
                    .replace(/^(https?:\/\/)?(www\.)?instagram\.com\//, '')
                    .replace(/^@/, '')
                    .replace(/\/$/, '');
            case 'discord':
                // Discord doesn't have profile URLs, just return as is
                return value;
            default:
                return value;
        }
    },

    /**
     * Add social from input
     */
    addSocial() {
        if (!this.activeSocialType) return;

        let value = document.querySelector('.pf-soc-input').value.trim();

        if (!value) return;

        // Clean the value based on social type
        value = this.cleanSocialValue(this.activeSocialType, value);

        const isUpdate = !!this.socials[this.activeSocialType];
        this.socials[this.activeSocialType] = value;

        if (isUpdate) {
            // Update existing entry
            this.updateSocialEntry(this.activeSocialType, value);
        } else {
            // Add new entry
            this.addSocialEntry(this.activeSocialType, value);
        }

        // Update hidden input
        this.setInput(this.activeSocialType === 'discord' ? 'discord_tag' : this.activeSocialType, value);

        this.cancelSocialInput();
    },

    /**
     * Cancel social input
     */
    cancelSocialInput() {
        document.querySelector('.pf-soc-input').value = '';
        document.querySelector('.pf-soc-input-wrap').classList.add('hidden');
        all('.pf-socials-add .soc-icon').forEach((icon) => icon.classList.remove('active'));
        this.activeSocialType = null;
    },

    /**
     * Add social entry to list (using template from blade)
     */
    addSocialEntry(type, value) {
        const template = byId(`pf-soc-tpl-${type}`);
        if (!template) return;

        const clone = template.content.cloneNode(true);
        clone.querySelector('.soc-entry__value').textContent = value;

        byId('pf-socials-list').append(clone);
    },

    /**
     * Update existing social entry value
     */
    updateSocialEntry(type, value) {
        all(`.soc-entry[data-social="${type}"] .soc-entry__value`).forEach((el) => el.textContent = value);
    },

    /**
     * Remove social entry
     */
    removeSocial(entry) {
        const type = entry.dataset.social;

        this.socials[type] = null;

        // Clear hidden input
        this.setInput(type === 'discord' ? 'discord_tag' : type, '');

        entry.remove();
    },

    /**
     * Toggle accordion
     */
    toggleAccordion() {
        all('.pf-accordion').forEach((el) => el.classList.toggle('pf-accordion--open'));
    },

    /**
     * Toggle premium service
     */
    togglePremium() {
        if (this.locked.premium) return;

        this.selectedServices.premium = !this.selectedServices.premium;

        if (this.selectedServices.premium) {
            // Premium includes pin and style, and its pin makes bump useless
            this.selectedServices.pin = false;
            this.selectedServices.style = false;
            this.selectedServices.bump = false;
        }

        this.renderServices();
    },

    /**
     * Toggle individual service
     */
    toggleService(type) {
        if (this.locked[type]) return;

        const services = this.selectedServices;
        if (services.premium && (type === 'pin' || type === 'style')) {
            // Taken out of premium: this service alone instead of the package
            services.premium = false;
            services[type] = true;
        } else if (type === 'bump' && (services.premium || services.pin)) {
            // Bump instead of the pin, which makes it useless; the style from premium stays
            if (services.premium) {
                services.premium = false;
                services.style = !this.locked.style;
            }
            services.pin = false;
            services.bump = true;
        } else {
            services[type] = !services[type];

            // Pin makes bump useless
            if (type === 'pin' && services.pin) {
                services.bump = false;
            }
        }

        this.renderServices();
    },

    /**
     * Select style preset
     */
    selectStyle(style) {
        this.selectedStyle = style;

        all('.pf-style-opt').forEach((opt) => opt.classList.toggle('pf-style-opt--selected', opt.dataset.style === style));

        // Update hidden input
        this.setInput('style_preset', style);

        // Update preview with new style
        this.renderPreview();
    },

    /**
     * Render services UI
     */
    renderServices() {
        const { premium, pin, bump, style } = this.selectedServices;

        // Premium
        byId('pf-premium')?.classList.toggle('pf-premium--selected', premium);
        byId('pf-cb-premium')?.classList.toggle('pf-cb--gold', premium);
        byId('pf-style-picker-premium')?.classList.toggle('hidden', !premium);

        // PIN (skip if locked by the server); premium includes it
        if (!this.locked.pin) {
            this.renderServiceCard('pin', {selected: pin && !premium, muted: premium});
        }

        // BUMP: a pinned party is on top anyway
        const covered = premium || pin;
        this.renderServiceCard('bump', {selected: bump && !covered, muted: covered, disabled: this.locked.bump});

        // STYLE; premium includes it
        const styleOn = style && !premium;
        this.renderServiceCard('style', {selected: styleOn, muted: premium, disabled: this.locked.style});
        byId('pf-style-picker-single')?.classList.toggle('hidden', !styleOn);

        // Update premium checkbox
        const premiumCard = byId('pf-premium');
        if (premiumCard) {
            const serviceId = premiumCard.dataset.serviceId;
            let premiumCheckbox = this.form.querySelector(`input[name="services[]"][value="${serviceId}"]`);
            if (!premiumCheckbox) {
                // Create hidden input for premium if needed
                premiumCheckbox = document.createElement('input');
                premiumCheckbox.type = 'checkbox';
                premiumCheckbox.name = 'services[]';
                premiumCheckbox.className = 'hidden';
                premiumCheckbox.value = serviceId;
                premiumCard.append(premiumCheckbox);
            }
            premiumCheckbox.checked = premium;
        }

        // Calculate and show total
        this.updateTotal();

        // Update publish button on step 3
        this.updatePublishButton();

        // Update live preview
        this.renderPreview();
    },

    /**
     * One paid service card and its checkbox (checked only when picked on its own, not through premium).
     * Disabled: the server doesn't sell it (pin slots are full, the party already has it). Muted: premium
     * includes it or the pin makes it useless; it stays clickable and the note says a click picks it instead
     */
    renderServiceCard(name, {selected, muted, disabled = false}) {
        const card = byId(`pf-svc-${name}`);
        if (!card) return;

        const showMuted = muted && !disabled;
        card.classList.toggle('pf-service--selected', selected);
        card.classList.toggle('pf-service--muted', showMuted);
        card.classList.toggle('pf-service--disabled', disabled);
        byId(`pf-cb-${name}`)?.classList.toggle('pf-cb--teal', selected);
        card.querySelector('.pf-svc-note')?.classList.toggle('hidden', !showMuted);
        all('input[type="checkbox"]', card).forEach((el) => el.checked = selected);
    },

    // ==========================================
    // Stepper Navigation
    // ==========================================

    /**
     * Go to a specific step
     */
    goToStep(step) {
        // Track forward moves only, so "back" clicks don't inflate the funnel
        if (step > this.currentStep || step === 0) {
            gtag('event', 'party_form_step', {
                'form_type': this.formType,
                'location': this.location,
                'step': step,
            });
        }

        this.currentStep = step;

        // Show the current step content; footer only for steps 1-3
        all('.pf-step-content, .pf-footer-step').forEach((el) => el.classList.add('hidden'));
        byId(`pf-step-${step}`)?.classList.remove('hidden');
        byId('pf-footer')?.classList.toggle('hidden', step < 1);
        if (step >= 1) {
            byId(`pf-footer-${step}`)?.classList.remove('hidden');
        }

        // Update progress bar
        this.updateProgressBar();

        // Update publish button on step 3
        this.updatePublishButton();
    },

    /**
     * Go to step with validation (for steps requiring role selection)
     */
    goToStepWithValidation(step) {
        if (!this.typeStrategy.validate(this)) {
            return;
        }
        this.goToStep(step);
    },

    /**
     * Update progress bar based on current step
     */
    updateProgressBar() {
        // The promote form has no progress; hide it on step 0
        if (!this.progress) return;
        this.progress.classList.toggle('hidden', this.currentStep === 0);
        if (this.currentStep === 0) return;

        // Update step indicators
        all('.pf-step', this.progress).forEach((stepEl) => {
            const stepNum = parseInt(stepEl.dataset.step);
            const dot = stepEl.querySelector('.pf-step-dot');

            stepEl.classList.toggle('done', stepNum < this.currentStep);
            stepEl.classList.toggle('active', stepNum === this.currentStep);
            dot.classList.toggle('active', stepNum === this.currentStep);
            dot.textContent = stepNum < this.currentStep ? '✓' : stepNum;
        });

        // Line after step N is done if current step > N
        all('.pf-step-line', this.progress).forEach((line, i) => {
            line.classList.toggle('done', this.currentStep > i + 1);
        });
    },

    /**
     * Update publish button text/style based on selected services
     */
    updatePublishButton() {
        const btn = byId('pf-btn-publish-3');
        if (!btn) return;

        const total = this.calculateTotal();

        // Promote mode has nothing to publish: the button only pays
        btn.classList.toggle('pf-btn-publish--pay', total > 0);
        if (total > 0) {
            btn.innerHTML = this.priceHtml(t(this.isPromote ? 'Оплатить' : 'Опубликовать и оплатить') + ' · ' + this.formatAmount(total));
        } else {
            btn.textContent = t(this.isPromote ? 'Выберите услугу' : 'Опубликовать');
        }
        if (this.isPromote) {
            btn.disabled = total === 0;
        }
    },

    /**
     * Calculate total price in cents
     */
    calculateTotal() {
        const { premium, pin, bump, style } = this.selectedServices;
        const prices = this.prices[this.currency];
        let total = 0;

        if (premium) {
            total = prices.premium;
        } else {
            if (pin) total += prices.pin;
            if (style) total += prices.style;
        }
        if (bump && !premium && !pin) {
            total += prices.bump;
        }

        return total;
    },

    /**
     * Handle publish button click
     */
    handlePublish() {
        if (this.isPromote) {
            if (this.calculateTotal() > 0) {
                gtag('event', 'promote_pay', this.publishEventParams());
                this.submitForm();
            }
            return;
        }

        // Validate roles before submit
        if (!this.typeStrategy.validate(this)) {
            // Go to step 1 to show error
            this.goToStep(1);
            return;
        }

        gtag('event', 'party_form_publish', this.publishEventParams());

        // Submit the form
        this.submitForm();
    },

    /**
     * GA params for publish events: the step it was sent from and the paid services picked
     */
    publishEventParams() {
        const total = this.calculateTotal();
        const params = {
            'form_type': this.formType,
            'location': this.location,
            'step': this.currentStep,
            'paid': total > 0 ? 'yes' : 'no',
            'services': Object.keys(this.selectedServices).filter(k => this.selectedServices[k]).join(',') || 'none',
            // "Also post in" queues create extra parties in one submit
            'queues_count': 1 + all('input[name="queues[]"]:checked', this.form).length,
        };

        // GA revenue needs an ISO currency, so Telegram Stars (XTR) go without value
        if (total > 0 && this.currency !== 'XTR') {
            params.value = total / 100;
            params.currency = 'USD';
        }

        return params;
    },

    /**
     * Update total price
     */
    updateTotal() {
        const total = this.calculateTotal();

        const totalBar = byId('pf-total');
        if (!totalBar) return;

        totalBar.classList.toggle('hidden', total === 0);
        if (total > 0) {
            totalBar.querySelector('.pf-total-amount').innerHTML = this.priceHtml(this.formatAmount(total));

            // Stars are unfamiliar to many: say how to pay and roughly what it costs
            byId('pf-pay-hint')?.classList.toggle('hidden', this.currency !== 'XTR');
            const usd = this.starUsd > 0 ? ' ≈ $' + (total * this.starUsd).toFixed(2) : '';
            const hintUsd = byId('pf-pay-hint-usd');
            if (hintUsd) hintUsd.innerHTML = this.priceHtml(' · ' + total + ' ⭐' + usd);
        }
    },

    /**
     * Submit form
     */
    async submitForm() {
        const formData = new FormData(this.form);
        const publishBtns = all('.pf-btn-publish');
        const originalHtml = new Map([...publishBtns].map((btn) => [btn, btn.innerHTML]));

        // Hide previous errors
        this.hideFormErrors();

        // Disable all publish buttons
        publishBtns.forEach((btn) => {
            btn.disabled = true;
            btn.textContent = t('Отправка...');
        });

        try {
            const response = await (await request(this.form.action, {method: 'POST', body: formData})).json();

            if (!this.isPromote) {
                gtag('event', 'party_created', this.publishEventParams());
            }

            if (response.redirect) {
                window.location.href = response.redirect;
            } else {
                window.location.reload();
            }
        } catch (err) {
            // Restore button texts
            publishBtns.forEach((btn) => {
                btn.disabled = false;
                btn.innerHTML = originalHtml.get(btn) || t('Опубликовать');
            });

            // Update step 3 button with price if needed
            this.updatePublishButton();

            if (err.status === 422 && err.data?.errors) {
                this.showFormErrors(Object.values(err.data.errors).flat());
            } else {
                this.showFormErrors([t('Произошла ошибка. Попробуйте ещё раз.')]);
            }
        }
    },

    /**
     * Show form errors in error block
     */
    showFormErrors(errors) {
        const errorBlock = byId('pf-form-errors');
        const errorList = errorBlock.querySelector('.pf-errors-list');

        errorList.replaceChildren(...errors.map((error) => {
            const li = document.createElement('li');
            li.textContent = error;
            return li;
        }));

        errorBlock.classList.remove('hidden');

        // Scroll to errors
        errorBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },

    /**
     * Hide form errors
     */
    hideFormErrors() {
        byId('pf-form-errors')?.classList.add('hidden');
    },

    /**
     * Escape HTML
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    },

    /**
     * Available style presets
     */
    stylePresets: ['gold', 'fire', 'ice', 'rainbow', 'neon', 'emerald'],

    /**
     * Initialize preview with summoner data
     */
    initPreview() {
        if (!this.summonerData) return;

        const data = this.summonerData;

        // Avatar and name are common, the rest is the form type's (party: level, rank, roles; team: team name)
        const avatar = byId('pf-pv-avatar-img');
        if (avatar) avatar.src = data.icon_url;
        setText('pf-pv-name', data.name);

        this.typeStrategy.renderPreview(this, data);
    },

    /**
     * Render live preview based on selected services
     */
    renderPreview() {
        const { premium, pin, style } = this.selectedServices;
        const hasAnything = premium || pin || style;

        const preview = byId('pf-preview');
        const card = byId('pf-preview-card');
        if (!preview || !card) return;

        // Show/hide preview
        preview.classList.toggle('hidden', !hasAnything);

        if (!hasAnything) return;

        // PIN and PRO badges
        const showPin = premium || pin;
        byId('pf-pv-pin')?.classList.toggle('hidden', !showPin);
        card.classList.toggle('has-pin', showPin);
        byId('pf-pv-pro')?.classList.toggle('hidden', !premium);

        // Apply style class to card (preview has its own style rules)
        card.classList.remove(...this.stylePresets.map(s => 'style-' + s));
        if (premium || style) {
            card.classList.add('style-' + this.selectedStyle);
        }
    }
};

// Delegated to the document, so bound once for every form the modal loads
EntityForm.bindGlobalEvents();

// Initialize on modal load
document.addEventListener('modal:loaded', function(e) {
    if (e.target.id === 'main-modal') EntityForm.init(e.detail.cta, e.detail.href);
});

// Cleanup on modal close
document.addEventListener('modal:hidden', function(e) {
    if (e.target.id === 'main-modal') EntityForm.destroy();
});
